import { PatientReferenceIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { ServiceLocationId } from '@dreryk/egabinet-healthcare-provider-published-language-api';

import { assertUnreachable } from '@dreryk/react-common';

import { checkIfPatientIdentifiesWithPESEL } from '../../../common/common.utils';

import { P1CommunicationFaultPlaceholder } from './P1CommunicationFaultPlaceholder';
import { PatientDataAccessRequestStep } from './PatientDataAccessRequestStep/PatientDataAccessRequestStep';
import { PatientDataAccessVerificationCodeForm } from './PatientDataAccessVerificationCodeForm/PatientDataAccessVerificationCodeForm';
import { PatientWithoutIkpPlaceholder } from './PatientWithoutIkpPlaceholder';
import { PatientWithoutPESELStatus } from './PatientWithoutPESELStatus';
import { useHistoryDownloadStep } from './PrescriptionsHistoryDownload.hooks';
import { HistoryDownloadStepName } from './PrescriptionsHistoryDownload.types';
import { PrescriptionsHistoryView } from './prescriptionsHistoryView/PrescriptionsHistoryView';

type PrescriptionsHistoryDownloadContentProps = {
   readonly patientId: number;
   readonly visitId: number;
   readonly locationId: ServiceLocationId;
};

const PrescriptionsHistoryDownloadContent = (props: PrescriptionsHistoryDownloadContentProps): JSX.Element => {
   const [currentStep, methods] = useHistoryDownloadStep();

   switch (currentStep.name) {
      case HistoryDownloadStepName.ACCESS_VERIFICATION:
      case HistoryDownloadStepName.PRESCRIPTIONS_HISTORY:
         return (
            <PrescriptionsHistoryView
               patientId={props.patientId}
               locationId={props.locationId}
               onFault={methods.goToAccessRequest}
               accessMode={currentStep.state?.accessMode}
            />
         );
      case HistoryDownloadStepName.ACCESS_REQUEST:
         return (
            <PatientDataAccessRequestStep
               patientId={props.patientId}
               visitId={props.visitId}
               onPatientDataRequestSuccess={methods.goToAccessCode}
               onPrescriptionHistoryDownloadRequest={methods.goToPrescriptionsHistory}
               onPatientIkpNotConfiguredFault={methods.goToIkpPlaceholder}
               onP1CommunicationFault={methods.goToP1CommunicationFaultStep}
               onPatientDataAccessVerificationFault={methods.goToP1CommunicationFaultStep}
               errorMessage={currentStep.state.errorMessage}
            />
         );
      case HistoryDownloadStepName.ACCESS_CODE:
         return (
            <PatientDataAccessVerificationCodeForm
               onGoBackToAuthorizerChoice={methods.goToAccessRequest}
               onP1CommunicationFault={methods.goToP1CommunicationFaultStep}
               onVerificationSuccess={methods.goToPrescriptionsHistory}
               applicationId={currentStep.state.applicationId}
               visitId={props.visitId}
            />
         );
      case HistoryDownloadStepName.NO_IKP_PLACEHOLDER:
         return <PatientWithoutIkpPlaceholder onGoBackToAuthorizerChoiceClick={methods.goToAccessRequest} />;
      case HistoryDownloadStepName.P1_COMMUNICATION_FAULT:
         return (
            <P1CommunicationFaultPlaceholder
               onTryAgainLinkClick={methods.goToPreviousStep}
               faultDescription={currentStep.state.faultDescription}
            />
         );
      default:
         return assertUnreachable(currentStep);
   }
};

type PrescriptionsHistoryDownloadProps = {
   readonly patientId: number;
   readonly visitId: number;
   readonly patientReferenceIdentifier: PatientReferenceIdentifier;
   readonly locationId: ServiceLocationId;
};

export const PrescriptionsHistoryDownload = (props: PrescriptionsHistoryDownloadProps): JSX.Element => {
   if (!checkIfPatientIdentifiesWithPESEL(props.patientReferenceIdentifier)) {
      return <PatientWithoutPESELStatus />;
   }

   return (
      <PrescriptionsHistoryDownloadContent
         patientId={props.patientId}
         visitId={props.visitId}
         locationId={props.locationId}
      />
   );
};
